import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import {
  useCohortProfileQuery,
  useCohortUsageQuery,
  useDeleteCohortMutation,
  useDeleteCohortProfileMutation,
} from '@verantos-dev/data-access-cohorts';
import { DialogButton } from '@verantos-dev/shared-components';
import { STATUS_LABEL_MAPPING } from '@verantos-dev/shared-components';
import { CohortDefinitionStatus } from '@verantos-dev/types-cohorts';

import { useCohortModalContext } from '../context/modalContext';

export const useDeleteCohortModal = () => {
  const { itemId, closeDialog } = useCohortModalContext();
  const { projectId } = useParams<{ projectId: string }>();
  // Gen-3 uses the keyed TanStack mutation so the project-page artifact manifest
  // + activity feed refresh on delete (OR-6111); legacy keeps the RTK mutation
  // untouched. Both hooks called unconditionally (rules of hooks).
  // TODO: cleanup with research_assistant_gen_3 ff — drop the RTK branch.
  const isGen3Enabled = useFeatureFlagSplit('research_assistant_gen_3', false) as boolean;
  const [rtkDeleteMutation, { isLoading: rtkIsLoading, error: rtkError }] =
    useDeleteCohortMutation();
  const gen3DeleteMutation = useDeleteCohortProfileMutation();
  const isLoading = isGen3Enabled ? gen3DeleteMutation.isPending : rtkIsLoading;
  const error = isGen3Enabled ? gen3DeleteMutation.error ?? undefined : rtkError;
  const {
    data: cohort,
    isLoading: loadingCohort,
    isFetching: isFetchingCohort,
  } = useCohortProfileQuery({
    cohortId: itemId || '',
    projectId: projectId || '',
  });

  const {
    data: usage,
    isLoading: loadingUsage,
    isFetching: isFetchingUsage,
  } = useCohortUsageQuery({
    cohortId: itemId || '',
    projectId: projectId || '',
  });

  const isInUse = usage?.isUsed ?? false;
  const analyticalDatasets = usage?.analyticalDatasets ?? [];
  const isCheckingUsage = loadingUsage || isFetchingUsage;

  const [saveButtonColor, setSaveButtonColor] = useState<DialogButton['color']>('destructive');

  const disableDeleteStatus = useMemo(() => {
    if (cohort?.latestStatus) {
      return {
        isDisabled:
          cohort.latestStatus === CohortDefinitionStatus.FINAL ||
          cohort.latestStatus === CohortDefinitionStatus.PROCESSING,
        status: STATUS_LABEL_MAPPING[cohort.latestStatus.toLowerCase()],
      };
    }
    return { isDisabled: false, status: 'Unknown' };
  }, [cohort]);

  const onSubmit = async () => {
    // Mirror the server guard: never fire the delete while the cohort is in use.
    if (!projectId || !itemId || isInUse) {
      return;
    }
    const deleteArgs = { projectId: projectId, cohortId: itemId };
    if (isGen3Enabled) {
      await gen3DeleteMutation.mutateAsync(deleteArgs);
    } else {
      await rtkDeleteMutation({ projectId: projectId, cohortId: itemId });
    }
    setSaveButtonColor('primary');
  };

  return {
    isDisableDeleteStatus: disableDeleteStatus.isDisabled,
    isInUse,
    analyticalDatasets,
    isSubmitting: isLoading,
    deleteCohortError: error,
    isInitialLoading: loadingCohort || isFetchingCohort || isCheckingUsage,
    saveButtonColor,
    isValidForSubmit:
      !disableDeleteStatus.isDisabled &&
      !isInUse &&
      !isCheckingUsage &&
      !loadingCohort &&
      !isFetchingCohort &&
      !isLoading,
    cohort,
    onClose: closeDialog,
    onSubmit,
  };
};
